import { useCallback } from 'react'
import { track } from '@ytest/ui'
import * as Sentry from '@sentry/react'
import type { Message, PendingAction, ToolActivity, ConversationSummary, DetailPanelContent } from '../types'
import type { ChatAction } from './useChatReducer'
import type { ToolCallRecord } from '../external-types'
import testOpsService from '../api'

// Translate a raw upstream/network error string into a human-friendly chat
// message. Avoids leaking stack traces or status codes to end users while
// still surfacing enough context for them to know what to do next.
function formatStreamError(rawMessage: string): string {
  const msg = rawMessage.toLowerCase()

  if (
    msg.includes('429') ||
    msg.includes('exhaust') ||
    msg.includes('rate limit') ||
    msg.includes('try again later')
  ) {
    return "I'm being rate-limited right now. Please wait a moment and try again — I'll automatically retry once the limit resets."
  }

  if (
    msg.includes('500') ||
    msg.includes('502') ||
    msg.includes('503') ||
    msg.includes('504') ||
    msg.includes('unavailable') ||
    msg.includes('bad gateway')
  ) {
    return "TestOps Command is having a momentary issue. Please try again in a few seconds."
  }

  if (
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('abort') ||
    msg.includes('connection')
  ) {
    return "I lost the connection mid-response. Check your network and try again."
  }

  if (msg.includes('timeout') || msg.includes('deadline')) {
    return "That request took too long and timed out. Please try again — if it keeps happening, the underlying tool may be slow or unreachable."
  }

  return `I couldn't complete that request right now.\n\n${rawMessage}`
}

export interface UseChatActionsConfig {
  messages: Message[]
  activeConversationId: string | null
  selectedPortfolioId: string
  draftStorageKey: string | null
  userId: string | null
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  streamAbortRef: React.MutableRefObject<AbortController | null>
  streamingContentMirror: React.MutableRefObject<string>
  streamingToolCallsRef: React.MutableRefObject<ToolCallRecord[]>
  dispatchChat: React.Dispatch<ChatAction>
  setToolActivities: React.Dispatch<React.SetStateAction<ToolActivity[]>>
  setStreamStatus: React.Dispatch<React.SetStateAction<string | null>>
  setPendingAction: React.Dispatch<React.SetStateAction<PendingAction | null>>
  setSearchParams: (fn: (prev: URLSearchParams) => URLSearchParams, opts?: { replace?: boolean }) => void
  createdInSessionRef: React.MutableRefObject<Set<string>>
  loadConversationHistory: () => void
  setConversationPendingDelete: React.Dispatch<React.SetStateAction<ConversationSummary | null>>
  deletingConversationId: string | null
  setDeletingConversationId: React.Dispatch<React.SetStateAction<string | null>>
  conversationPendingDelete: ConversationSummary | null
  conversationHistory: ConversationSummary[]
  loadActivePins: () => void
  input: string
  attachedFile: File | null
  setAttachedFile: React.Dispatch<React.SetStateAction<File | null>>
  setDetailPanelContent: React.Dispatch<React.SetStateAction<DetailPanelContent | null>>
  panelHistoryRef: React.MutableRefObject<Map<string, DetailPanelContent>>
}

export function useChatActions(config: UseChatActionsConfig) {
  const {
    messages,
    activeConversationId,
    selectedPortfolioId,
    draftStorageKey,
    userId,
    streamAbortRef,
    streamingContentMirror,
    streamingToolCallsRef,
    dispatchChat,
    setToolActivities,
    setStreamStatus,
    setPendingAction,
    setSearchParams,
    createdInSessionRef,
    loadConversationHistory,
    setConversationPendingDelete,
    setDeletingConversationId,
    conversationPendingDelete,
    conversationHistory,
    loadActivePins,
    input,
    attachedFile,
    setAttachedFile,
    setDetailPanelContent,
    panelHistoryRef,
  } = config

  const sendMessage = useCallback(async (messageText: string, approvalNonce?: string | null) => {
    const trimmedMessage = messageText.trim()
    if (!trimmedMessage && !attachedFile) return

    // Capture and clear attached file before async work
    const fileToSend = attachedFile
    setAttachedFile(null)

    // Abort any in-flight stream before starting a new one so we don't
    // leak readers or dispatch stale state updates.
    streamAbortRef.current?.abort()
    const controller = new AbortController()
    streamAbortRef.current = controller

    const displayContent = fileToSend
      ? (trimmedMessage ? `${trimmedMessage}\n\n📎 ${fileToSend.name}` : `📎 ${fileToSend.name}`)
      : trimmedMessage

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: displayContent,
      timestamp: new Date()
    }

    dispatchChat({ type: 'append-message', payload: userMessage })
    dispatchChat({ type: 'set-input', payload: '' })
    dispatchChat({ type: 'set-typing', payload: true })
    if (draftStorageKey) sessionStorage.removeItem(draftStorageKey)
    track('TestOps Hub Message Sent', {
      hasFile: !!fileToSend,
      messageLength: trimmedMessage.length,
      isApproval: !!approvalNonce,
    })

    // Clear streaming state
    setToolActivities([])
    setStreamStatus(null)
    streamingContentMirror.current = ''
    streamingToolCallsRef.current = []
    dispatchChat({ type: 'streaming-clear' })
    setPendingAction(null)

    try {
      const history = messages.map(message => ({
        role: message.role,
        content: message.content,
      }))

      let streamSources: any[] = []

      await testOpsService.chatStream(
        trimmedMessage || (fileToSend ? `Analyze the attached file: ${fileToSend.name}` : ''),
        history,
        activeConversationId,
        selectedPortfolioId || null,
        approvalNonce ?? null,
        fileToSend ?? undefined,
        {
          onStatus: (message) => {
            setStreamStatus(message)
          },
          onToolCall: (data) => {
            setStreamStatus(null)
            Sentry.addBreadcrumb({
              category: 'testops.tool_call',
              message: `${data.provider}/${data.tool}`,
              level: 'info',
              data: { call_id: data.call_id, provider: data.provider },
            })
            // Transient activity bar entry — keyed by call_id so the matching
            // tool_result event can update the same row.
            setToolActivities((prev) => [
              ...prev,
              {
                id: data.call_id,
                tool: data.tool,
                provider: data.provider,
                arguments: data.args,
                status: 'running',
                timestamp: new Date(),
              },
            ])
          },
          onToolResult: (data) => {
            // Track tool result for analytics
            Sentry.addBreadcrumb({
              category: 'testops.tool_result',
              message: `${data.provider}/${data.tool}: ${data.status}`,
              level: data.status === 'error' ? 'error' : 'info',
              data: { call_id: data.call_id, status: data.status, duration_ms: data.duration_ms },
            })
            if (data.status === 'error') {
              track('TestOps Tool Error', {
                provider: data.provider,
                tool: data.tool,
                error: data.error?.slice(0, 200),
              })
            }
            // Persistent record — attached to the final assistant message on
            // stream completion so the inspectable card survives reload.
            streamingToolCallsRef.current = [
              ...streamingToolCallsRef.current.filter((r) => r.call_id !== data.call_id),
              data,
            ]
            // Also update the transient activity bar so the in-progress row
            // flips from spinner → success/error/empty/truncated.
            setToolActivities((prev) =>
              prev.map((activity) =>
                activity.id === data.call_id
                  ? {
                      ...activity,
                      status:
                        data.status === 'error'
                          ? 'error'
                          : data.status === 'running'
                            ? 'running'
                            : 'success',
                      summary: data.summary,
                    }
                  : activity
              )
            )
          },
          onContent: (content) => {
            // Mirror to ref so the onDone closure (below) can read the
            // accumulated content without depending on the latest state.
            streamingContentMirror.current += content
            // Push the delta into reducer state so the in-progress assistant
            // bubble re-renders with the new tokens.
            dispatchChat({ type: 'streaming-delta', payload: content })
          },
          onContentReplace: (content) => {
            // Hallucination guard: the backend detected fabricated data
            // in the streamed response and wants to replace it entirely.
            streamingContentMirror.current = content
            dispatchChat({ type: 'streaming-replace', payload: content })
          },
          onPanelUpdate: (panelEvent) => {
            track('TestOps Panel Update', {
              action: panelEvent.action,
              resourceType: panelEvent.resourceType,
              provider: panelEvent.provider,
            })
            const incoming = {
              resourceKey: panelEvent.resourceKey,
              resourceType: panelEvent.resourceType as DetailPanelContent['resourceType'],
              title: panelEvent.title,
              provider: panelEvent.provider,
              data: panelEvent.content as Record<string, unknown>,
            }

            // Always update the history ref (even for non-active resources)
            // so switching tabs shows the latest data
            if (panelEvent.action === 'merge' || panelEvent.action === 'patch') {
              const existing = panelHistoryRef?.current?.get(incoming.resourceKey)
              if (existing) {
                const existingData = (existing.data || {}) as Record<string, unknown>
                const newData = (incoming.data || {}) as Record<string, unknown>
                if (panelEvent.action === 'merge') {
                  const merged = { ...existingData, ...newData }
                  panelHistoryRef?.current?.set(incoming.resourceKey, { ...existing, data: merged })
                } else {
                  const patched = { ...existingData }
                  for (const [k, v] of Object.entries(newData)) {
                    if (v != null) patched[k] = v
                  }
                  panelHistoryRef?.current?.set(incoming.resourceKey, { ...existing, data: patched })
                }
              }
            }

            setDetailPanelContent((prev) => {
              if (panelEvent.action === 'open') {
                if (prev && prev.resourceKey === incoming.resourceKey) {
                  const prevData = (prev.data || {}) as Record<string, unknown>
                  const newData = (incoming.data || {}) as Record<string, unknown>
                  const preserved: Record<string, unknown> = {}
                  for (const [k, v] of Object.entries(prevData)) {
                    if (k.startsWith('_')) preserved[k] = v
                  }
                  return { ...incoming, data: { ...newData, ...preserved } }
                }
                return incoming
              }

              if (panelEvent.action === 'merge') {
                if (prev && prev.resourceKey === incoming.resourceKey) {
                  const merged = { ...(prev.data || {}) as Record<string, unknown>, ...(incoming.data || {}) as Record<string, unknown> }
                  return { ...prev, data: merged, title: incoming.title || prev.title }
                }
                // Switch to the updated tab so user sees the change
                const mergedEntry = panelHistoryRef?.current?.get(incoming.resourceKey)
                if (mergedEntry) return mergedEntry
                return prev
              }

              if (panelEvent.action === 'patch') {
                if (prev && prev.resourceKey === incoming.resourceKey) {
                  const patched = { ...(prev.data || {}) as Record<string, unknown> }
                  for (const [k, v] of Object.entries((incoming.data || {}) as Record<string, unknown>)) {
                    if (v != null) patched[k] = v
                  }
                  return { ...prev, data: patched }
                }
                // Switch to the updated tab so user sees the change
                const patchedEntry = panelHistoryRef?.current?.get(incoming.resourceKey)
                if (patchedEntry) return patchedEntry
                return prev
              }

              return incoming
            })
          },
          onPendingAction: (action) => {
            console.log('[TestOps] pending_action received:', action)
            setPendingAction(action)
          },
          onDone: async (data) => {
            console.log('[TestOps] done event, pending_action:', data.pending_action ? 'YES' : 'NO', data.pending_action)
            streamSources = data.sources || []
            if (data.conversation_id !== activeConversationId) {
              createdInSessionRef.current.add(data.conversation_id)
              dispatchChat({ type: 'set-active-conversation', payload: data.conversation_id })
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                next.set('conversation', data.conversation_id)
                // Strip any deep-link `?prompt=` that the dashboard added.
                // The prefill effect on TestOpsHub already cleared it from the
                // URL bar via window.history.replaceState, but React Router's
                // internal state still holds it — so without this delete, the
                // setSearchParams below would write it back into the URL and a
                // refresh would re-fire the prompt. createdInSessionRef.add
                // above shields the restoreConversation effect from wiping the
                // just-rendered message when this re-render fires.
                next.delete('prompt')
                return next
              }, { replace: true })
            }
            if (data.pending_action) {
              setPendingAction(data.pending_action)
            }

            const finalContent = streamingContentMirror.current
            const finalToolCalls = streamingToolCallsRef.current
            if (finalContent) {
              const aiResponse: Message = {
                id: crypto.randomUUID(),
                role: 'assistant',
                content: finalContent,
                timestamp: new Date(),
                sources: streamSources,
                toolCalls: finalToolCalls.length > 0 ? finalToolCalls : undefined,
              }
              // 'append-message' also clears streamingAssistantContent so the
              // in-progress bubble disappears the moment the final one renders.
              dispatchChat({ type: 'append-message', payload: aiResponse })
            } else {
              // Stream produced nothing — clear any leftover in-progress state.
              dispatchChat({ type: 'streaming-clear' })
            }
            streamingContentMirror.current = ''
            streamingToolCallsRef.current = []

            // Clear tool activities after message is appended
            setToolActivities([])
            setStreamStatus(null)

            await loadConversationHistory()
            void loadActivePins()
          },
          onError: (message) => {
            console.error('Stream error:', message)
            track('TestOps Stream Error', { error: message?.slice(0, 200) })
            Sentry.captureMessage(`TestOps stream error: ${message?.slice(0, 200)}`, 'error')
            const isConversationTooLong = message === '__CONVERSATION_TOO_LONG__'
            dispatchChat({
              type: 'append-message',
              payload: {
                id: crypto.randomUUID(),
                role: 'assistant',
                content: isConversationTooLong
                  ? "This conversation has grown too long for me to continue safely. Please **start a new conversation** to keep going — your integrations and context will be fully available."
                  : formatStreamError(message),
                timestamp: new Date(),
                sources: [],
                isError: true,
              },
            })
            setToolActivities([])
            setStreamStatus(null)
            streamingToolCallsRef.current = []
          },
        },
        controller.signal,
      )
    } catch (error) {
      // Aborted streams are expected — don't show an error message.
      if (error instanceof DOMException && error.name === 'AbortError') return
      console.error('Failed to send message to TestOps Hub:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'TestOps Hub encountered an error. Please try again.'
      dispatchChat({
        type: 'append-message',
        payload: {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: formatStreamError(errorMessage),
          timestamp: new Date(),
          sources: [],
          isError: true,
        },
      })
    } finally {
      dispatchChat({ type: 'set-typing', payload: false })
    }
  }, [activeConversationId, attachedFile, draftStorageKey, loadConversationHistory, messages, selectedPortfolioId, setSearchParams])

  const handleNewChat = useCallback(() => {
    // Abort any in-flight stream so the old conversation's callbacks
    // don't clobber the fresh chat state.
    streamAbortRef.current?.abort()
    setPendingAction(null)
    setDetailPanelContent(null)
    track('TestOps New Conversation')
    dispatchChat({ type: 'start-new-chat' })
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('conversation')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const handleOpenConversation = useCallback((conversationId: string) => {
    // Abort any in-flight stream so the old conversation's callbacks
    // don't write into the new conversation's state.
    streamAbortRef.current?.abort()
    // Remove from the "created this session" set so it can be hydrated from the server
    createdInSessionRef.current.delete(conversationId)
    // Clear panel — the new conversation's panel will be restored via hydrateConversation
    setDetailPanelContent(null)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('conversation', conversationId)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const handleDeleteConversation = useCallback(async (conversationId: string) => {
    const conversation = conversationHistory.find((item) => item.id === conversationId)
    if (!conversation) return
    setConversationPendingDelete(conversation)
  }, [conversationHistory])

  const confirmDeleteConversation = useCallback(async () => {
    if (!conversationPendingDelete) return

    const conversationId = conversationPendingDelete.id
    setDeletingConversationId(conversationId)
    try {
      await testOpsService.deleteConversation(conversationId)
      // Clean up panel state and draft for deleted conversation.
      // Drafts live in sessionStorage and are user-scoped (see TestOpsHub.tsx);
      // the previous localStorage cleanup never matched anything.
      try { localStorage.removeItem(`testops-panel:${conversationId}`) } catch {}
      if (userId) {
        try { sessionStorage.removeItem(`testops-hub-draft:${userId}:${conversationId}`) } catch {}
      }
      await loadConversationHistory()

      if (conversationId === activeConversationId) {
        handleNewChat()
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
    } finally {
      setDeletingConversationId(null)
      setConversationPendingDelete(null)
    }
  }, [activeConversationId, conversationPendingDelete, handleNewChat, loadConversationHistory, userId])

  const handleSend = useCallback(async () => {
    await sendMessage(input)
  }, [input, sendMessage])

  const handleStopGenerating = useCallback(() => {
    // Capture partial content before aborting so it isn't lost.
    const partialContent = streamingContentMirror.current
    const partialToolCalls = [...streamingToolCallsRef.current]

    if (partialContent) {
      dispatchChat({
        type: 'append-message',
        payload: {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: partialContent,
          timestamp: new Date(),
          toolCalls: partialToolCalls.length > 0 ? partialToolCalls : undefined,
          isPartial: true,
        },
      })
    } else {
      dispatchChat({ type: 'streaming-clear' })
    }

    dispatchChat({ type: 'set-typing', payload: false })
    setToolActivities([])
    setStreamStatus(null)
    streamingContentMirror.current = ''
    streamingToolCallsRef.current = []

    // Abort after dispatching so the catch block's AbortError guard
    // doesn't race with our cleanup.
    streamAbortRef.current?.abort()

    config.inputRef.current?.focus()
  }, [dispatchChat, setToolActivities, setStreamStatus])

  const handleRetry = useCallback(async () => {
    // Find the last user message (the one before the error assistant message).
    const lastMessages = messages.slice(-2)
    const userMsg = lastMessages.find((m) => m.role === 'user')
    if (!userMsg) return

    const retryContent = userMsg.content
    dispatchChat({ type: 'remove-last-exchange' })

    // Use queueMicrotask to ensure the reducer dispatch has been processed
    // before re-sending, instead of a fragile setTimeout.
    await new Promise<void>((resolve) => queueMicrotask(resolve))
    await sendMessage(retryContent)
  }, [messages, sendMessage])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return {
    sendMessage,
    handleNewChat,
    handleOpenConversation,
    handleDeleteConversation,
    confirmDeleteConversation,
    handleSend,
    handleStopGenerating,
    handleRetry,
    handleKeyDown,
  }
}
